import { useCallback } from 'react';

import { type AnalyticsEventMap, captureEvent } from '@/services/analytics';

import {
  buildAnalyticsEvent,
  buildCopyEventData,
  type CopyContentType
} from '@/features/data-hub/lib/ExportAnalyticsService';
import type { ExportSettingsType } from '@/features/data-hub/types/context.types';
import type { ExportOption } from '@/features/data-hub/types/exportConfig.types';
import { useAppSelector } from '@/store/hooks';

const COPY_CONTENT_TYPE_MAP: Partial<Record<string, CopyContentType>> = {
  gx_api: 'api',
  curl: 'curl',
  python: 'python',
  power_query: 'powerquery',
  snowflake: 'snowflake'
};

const useExportAnalytics = (
  exportOption: ExportOption | null,
  exportSettings: ExportSettingsType
) => {
  const symbols = useAppSelector(state => state.symbolSelector.symbols);

  const capturePosthogEvent = useCallback(() => {
    const { eventName, data } = buildAnalyticsEvent({ exportOption, symbols, exportSettings });
    captureEvent(eventName as keyof AnalyticsEventMap, data);
  }, [exportOption, symbols, exportSettings]);

  const captureCopyEvent = useCallback(() => {
    const contentType = exportOption?.id ? COPY_CONTENT_TYPE_MAP[exportOption.id] : undefined;
    if (!contentType) return;
    const { eventName, data } = buildCopyEventData(
      contentType,
      exportOption,
      symbols,
      exportSettings
    );
    captureEvent(eventName as keyof AnalyticsEventMap, data);
  }, [exportOption, symbols, exportSettings]);

  return { capturePosthogEvent, captureCopyEvent };
};

export default useExportAnalytics;
